import React, { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { ExitTicketActive, SelectOption } from '../types';
import { SllLesson, SllModule, SllSectionFragment, StudyPlan, SllCourse } from '@api/graphql-codegen';
export type SelectionLevel = 'Lesson' | 'Content' | null;

type StandaloneContext = {
  content: undefined | SllModule;
  lesson: undefined | SllLesson;
  selectionLevel: SelectionLevel;
  setContent: (content: SllModule | undefined) => void;
  setLesson: (lesson: SllLesson | undefined) => void;
  course: undefined | SllCourse;
  setCourse: (lesson: SllCourse | undefined) => void;
  setSelectionLevel: (level: SelectionLevel) => void;
  clear: () => void;
  isPreview?: boolean;
  setIsPreview: (isPreview: boolean) => void;
  pageNumber: number;
  setPageNumber: (pageNumber: number) => void;
  totalPages: number;
  setTotalPages: (totalPages: number) => void;
  lastContent: boolean;
  setLastContent: (lastContent: boolean) => void;
  accountLocked?: boolean;
  setAccountLocked: (accountLocked: boolean) => void;
  exitTickets: ExitTicketActive[];
  setExitTickets: (exitTickets: ExitTicketActive[]) => void;
  showExitModal?: boolean;
  setShowExitModal: (showExitModal: boolean) => void;
  currentStudyplan: undefined | StudyPlan;
  setCurrentStudyplan: (studyplan: StudyPlan) => void;
  showExitTicket?: boolean;
  setShowExitTicket: (showExitTicket: boolean) => void;
  isViewExitTicket?: boolean;
  setIsViewExitTicket: (isViewExitTicket: boolean) => void;
  showReview?: boolean;
  setShowReview: (showReview: boolean) => void;
  showCompleteUnit?: boolean;
  setShowCompleteUnit: (showCompleteUnit: boolean) => void;
  section?: undefined | SllSectionFragment;
  setSection: (section: SllSectionFragment | undefined) => void;
  showExerciseModal?: boolean;
  setShowExerciseModal: (showExerciseModal: boolean) => void;
  startedAlternateLesson?: boolean;
  setStartedAlternateLesson: (startedAlternateLesson: boolean) => void;
  isPreProgramQuiz: boolean;
  setIsPreProgramQuiz: (isPreProgramQuiz: boolean) => void;
  quizModules: SllModule[];
  setQuizModules: (quizModules: SllModule[]) => void;
  grade: SelectOption | undefined;
  setGrade: (grade : SelectOption | undefined) => void;
  exitTicketActive: ExitTicketActive | undefined,
  setExitTicketActive: (exitTicketActive: ExitTicketActive | undefined) => void,
  isNewLessonLayout?: boolean;
  setIsNewLessonLayout: (isNewLessonLayout: boolean) => void;
  isPlatformTower?: boolean;
  setIsPlatformTower: (isPlatformTower: boolean) => void;
  programId?: string;
  setProgramId: (programId: string) => void;
  programName?: string;
  setProgramName: (programName: string) => void;
  mcId?: string;
  setMcId: (mcId: string) => void;
  /** The grade of the card the lesson was launched from, when a link carried it. */
  launchedGrade?: string;
  setLaunchedGrade: (launchedGrade: string) => void;
  mcName?: string;
  setMcName: (mcName: string) => void;
  collectionId?: string;
  setCollectionId: (collectionId: string) => void;
  collectionName?: string;
  setCollectionName: (collectionName: string) => void;
  /** OP-3133: how the open lesson was reached ("prettyURL" for a share link). */
  accessMethod?: string;
  setAccessMethod: (accessMethod: string | undefined) => void;
  completeModal: boolean;
  completeModalManualOnly: boolean;
  completeModalKey: number;
  completeModalShouldExitRef: React.MutableRefObject<boolean>;
  openCompleteModal: (
    shouldExitPresentation?: boolean,
    manualCompleteOnly?: boolean,
  ) => void;
  closeCompleteModal: () => void;
  setExitPresentationHandler: (handler: (() => void) | null) => void;
  requestExitPresentation: () => void;
  setSilentMarkCompleteHandler: (
    handler: (() => void | Promise<void>) | null,
  ) => void;
  requestSilentMarkComplete: () => void;
};

const noop = () => {};
const defaultState: StandaloneContext = {
  content: undefined,
  setContent: noop,
  lesson: undefined,
  setLesson: noop,
  course: undefined,
  setCourse: noop,
  clear: noop,
  selectionLevel: null,
  setSelectionLevel: noop,
  isPreview: false,
  setIsPreview: noop,
  pageNumber: 1,
  setPageNumber: noop,
  totalPages: 0,
  setTotalPages: noop,
  lastContent: false,
  setLastContent: noop,
  accountLocked: false,
  setAccountLocked: noop,
  exitTickets: [],
  setExitTickets: noop,
  showExitModal: false,
  setShowExitModal: noop,
  currentStudyplan: undefined,
  setCurrentStudyplan: noop,
  showExitTicket: false,
  setShowExitTicket: noop,
  isViewExitTicket: false,
  setIsViewExitTicket: noop,
  showReview: false,
  setShowReview: noop,
  showCompleteUnit: false,
  setShowCompleteUnit: noop,
  section: undefined,
  setSection: noop,
  showExerciseModal: false,
  setShowExerciseModal: noop,
  startedAlternateLesson: false,
  setStartedAlternateLesson: noop,
  quizModules: [],
  setQuizModules: noop,
  isPreProgramQuiz: false,
  setIsPreProgramQuiz: noop,
  grade: undefined,
  setGrade: noop,
  exitTicketActive: undefined,
  setExitTicketActive: noop,
  isNewLessonLayout: false,
  setIsNewLessonLayout: noop,
  isPlatformTower: false,
  setIsPlatformTower: noop,
  programId: undefined,
  setProgramId: noop,
  programName: undefined,
  setProgramName: noop,
  mcId: undefined,
  setMcId: noop,
  launchedGrade: undefined,
  setLaunchedGrade: noop,
  mcName: undefined,
  setMcName: noop,
  collectionId: undefined,
  setCollectionId: noop,
  collectionName: undefined,
  setCollectionName: noop,
  accessMethod: undefined,
  setAccessMethod: noop,
  completeModal: false,
  completeModalManualOnly: false,
  completeModalKey: 0,
  completeModalShouldExitRef: { current: false },
  openCompleteModal: noop,
  closeCompleteModal: noop,
  setExitPresentationHandler: noop,
  requestExitPresentation: noop,
  setSilentMarkCompleteHandler: noop,
  requestSilentMarkComplete: noop,
};
const StandaloneContentContext = createContext(defaultState);

type Props = {
  children: ReactNode;
}

export const CourseContentProvider = ({ children }: Props) => {
  const [content, setContent] = useState<SllModule | undefined>(defaultState.content);
  const [lesson, setLesson] = useState<SllLesson | undefined>(defaultState.lesson);
  const [course, setCourse] = useState<SllCourse | undefined>(defaultState.course);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [selectionLevel, setSelectionLevel] = useState<SelectionLevel>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [lastContent, setLastContent] = useState<boolean>(false);
  const [accountLocked, setAccountLocked] = useState<boolean>(false);
  const [exitTickets, setExitTickets] = useState<ExitTicketActive[]>(defaultState.exitTickets);
  const [showExitModal, setShowExitModal] = useState<boolean>(false);
  const [currentStudyplan, setCurrentStudyplan] = useState<StudyPlan | undefined>();
  const [showExitTicket, setShowExitTicket] = useState<boolean>(false);
  const [isViewExitTicket, setIsViewExitTicket] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showCompleteUnit, setShowCompleteUnit] = useState(false);
  const [section, setSection] = useState<SllSectionFragment | undefined>(defaultState.section);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [startedAlternateLesson, setStartedAlternateLesson] = useState(false);
  const [quizModules, setQuizModules] = useState<SllModule[]>(defaultState.quizModules);
  const [isPreProgramQuiz, setIsPreProgramQuiz] = useState<boolean>(defaultState.isPreProgramQuiz);
  const [grade, setGrade] = useState<SelectOption | undefined>(defaultState.grade);
  const [exitTicketActive, setExitTicketActive] = useState<ExitTicketActive | undefined>(defaultState.exitTicketActive);
  const [isNewLessonLayout, setIsNewLessonLayout] = useState(false);
  const [isPlatformTower, setIsPlatformTower] = useState(false);
  const [programId, setProgramId] = useState(defaultState.programName);
  const [programName, setProgramName] = useState(defaultState.programName);
  const [mcId, setMcId] = useState(defaultState.mcId);
  const [launchedGrade, setLaunchedGrade] = useState(defaultState.launchedGrade);
  const [mcName, setMcName] = useState(defaultState.mcName);
  const [collectionId, setCollectionId] = useState(defaultState.collectionId);
  const [collectionName, setCollectionName] = useState(defaultState.collectionName);
  const [accessMethod, setAccessMethod] = useState<string | undefined>(defaultState.accessMethod);
  const [completeModal, setCompleteModal] = useState(false);
  const [completeModalManualOnly, setCompleteModalManualOnly] = useState(false);
  const [completeModalKey, setCompleteModalKey] = useState(0);
  const completeModalShouldExitRef = useRef(false);
  const openCompleteModal = useCallback(
    (shouldExitPresentation = false, manualCompleteOnly = false) => {
      if (shouldExitPresentation) {
        // Durable until Mark Complete / Cancel — must not be cleared by silent auto-complete.
        completeModalShouldExitRef.current = true;
        setCompleteModalManualOnly(true);
        setCompleteModalKey((key) => key + 1);
        setCompleteModal(true);
        if (typeof console !== "undefined") {
          console.info("[OP-complete] openCompleteModal EXIT intent", {
            manualCompleteOnly: true,
          });
        }
        return;
      }
      if (completeModalShouldExitRef.current) {
        if (typeof console !== "undefined") {
          console.info(
            "[OP-complete] openCompleteModal stay blocked — exit intent active",
          );
        }
        return;
      }
      setCompleteModalManualOnly(manualCompleteOnly);
      setCompleteModalKey((key) => key + 1);
      setCompleteModal(true);
    },
    [],
  );
  const closeCompleteModal = useCallback(() => {
    completeModalShouldExitRef.current = false;
    setCompleteModalManualOnly(false);
    setCompleteModal(false);
  }, []);
  const exitPresentationHandlerRef = useRef<(() => void) | null>(null);
  const setExitPresentationHandler = useCallback(
    (handler: (() => void) | null) => {
      exitPresentationHandlerRef.current = handler;
    },
    [],
  );
  const requestExitPresentation = useCallback(() => {
    const handler = exitPresentationHandlerRef.current;
    if (handler) {
      handler();
    } else if (typeof console !== "undefined") {
      console.warn(
        "[OP-complete] requestExitPresentation: no host handler registered",
      );
    }
  }, []);

  const silentMarkCompleteHandlerRef = useRef<
    (() => void | Promise<void>) | null
  >(null);
  const setSilentMarkCompleteHandler = useCallback(
    (handler: (() => void | Promise<void>) | null) => {
      silentMarkCompleteHandlerRef.current = handler;
    },
    [],
  );
  const requestSilentMarkComplete = useCallback(() => {
    void silentMarkCompleteHandlerRef.current?.();
  }, []);

  const clear = useCallback(() => {
    setContent(undefined);
    setLesson(undefined);
  }, []);

  return (
    <StandaloneContentContext.Provider value={{
      content,
      lesson,
      setContent,
      setLesson,
      course,
      setCourse,
      clear,
      selectionLevel,
      setSelectionLevel,
      isPreview,
      setIsPreview,
      pageNumber,
      setPageNumber,
      totalPages,
      setTotalPages,
      lastContent,
      setLastContent,
      accountLocked,
      setAccountLocked,
      exitTickets,
      setExitTickets,
      showExitModal,
      setShowExitModal,
      currentStudyplan,
      setCurrentStudyplan,
      showExitTicket,
      setShowExitTicket,
      isViewExitTicket,
      setIsViewExitTicket,
      showReview,
      setShowReview,
      showCompleteUnit,
      setShowCompleteUnit,
      section,
      setSection,
      showExerciseModal,
      setShowExerciseModal,
      startedAlternateLesson,
      setStartedAlternateLesson,
      quizModules,
      setQuizModules,
      isPreProgramQuiz,
      setIsPreProgramQuiz,
      grade, setGrade,
      exitTicketActive,
      setExitTicketActive,
      isNewLessonLayout,
      setIsNewLessonLayout,
      isPlatformTower,
      setIsPlatformTower,
      programId,
      setProgramId,
      programName,
      setProgramName,
      mcId,
      setMcId,
      launchedGrade,
      setLaunchedGrade,
      mcName,
      setMcName,
      collectionId,
      setCollectionId,
      collectionName,
      setCollectionName,
      accessMethod,
      setAccessMethod,
      completeModal,
      completeModalManualOnly,
      completeModalKey,
      completeModalShouldExitRef,
      openCompleteModal,
      closeCompleteModal,
      setExitPresentationHandler,
      requestExitPresentation,
      setSilentMarkCompleteHandler,
      requestSilentMarkComplete,
    }}>
      {children}
    </StandaloneContentContext.Provider>
  );
}

export const useStandaloneContent = () => useContext(StandaloneContentContext);
